import {useCallback, useContext, useMemo} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {getEndDateErrors, getStartDateErrors} from 'src/modules/create-account/steps/license-create/components/utils';
import {CONCURRENT_USERS_REQUIRED} from 'src/modules/create-account/steps/license-create/constants';
import {LicenseType as LicenseTypes} from 'src/openapi/admin/models/LicenseType';

import type {ValidationContextType} from 'src/contexts/validation-context';
import type {LicenseUpdateRequest} from 'src/openapi/admin/models/LicenseUpdateRequest';

export const useValidateLicense = () => {
    const {addError, removeError, validationErrors} = useContext<ValidationContextType>(ValidationContext);
    const {isToday, isFutureDate, isAfterDate} = useDateTime();

    const validateLicenseRequest = useCallback((saveLicenseRequest: LicenseUpdateRequest, validateUnfilled = false) => {
        const {startDate, endDate, licenseType, concurrentUserCount} = saveLicenseRequest;
        let hasErrors = false;

        const startError = getStartDateErrors({
            startDate,
            validateEmpty: validateUnfilled,
        });

        if (startError) {
            addError({code: startError.code, message: startError.message, controlId: 'license-start-date'});
            hasErrors = true;
        } else {
            removeError('license-start-date');
        }

        const endError = getEndDateErrors({
            startDate,
            endDate,
            validateEmpty: validateUnfilled,
            isToday,
            isFutureDate,
            isAfterDate,
        });

        if (endError) {
            removeError('license-end-date');
            addError({code: endError.code, message: endError.message, controlId: 'license-end-date'});
            hasErrors = true;
        } else {
            removeError('license-end-date');
        }

        if (licenseType === LicenseTypes.CU && concurrentUserCount || licenseType !== LicenseTypes.CU) {
            removeError('license-seats');
        } else if (validateUnfilled) {
            addError({
                code: 'CONCURRENT_USERS_REQUIRED',
                controlId: 'license-seats',
                message: CONCURRENT_USERS_REQUIRED,
            });
            hasErrors = true;
        } else {
            removeError('license-seats');
        }

        return hasErrors;
    }, [isFutureDate, isAfterDate, isToday, removeError, addError]);

    const hasValidationErrors = useMemo(() => !!validationErrors.length, [validationErrors]);

    return {validateLicenseRequest, hasValidationErrors};
};
